// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useEffect, lazy, Suspense } from 'react';
import type { UIAssistStatusResponse } from '@vn-grc/shared';
import { apiClient } from '../../../services/api-client.js';

/**
 * UI Assist context provider.
 * Checks feature flag + permission, then conditionally renders the overlay.
 * Uses dynamic import for zero overhead when disabled.
 * Sprint 17.8.0: UI Assist
 */

/**
 * The overlay chunk's loader, exported so a test can assert it is never called
 * while the server reports the overlay disabled (Sprint 63.22.0 AC7). Nothing
 * else should call it.
 */
export const loadUIAssistOverlay = () =>
  import('./UIAssistOverlay.js').then((m) => ({ default: m.UIAssistOverlay }));

/** Indirection the test can spy on; production always uses `loadUIAssistOverlay`. */
export const overlayLoader = { load: loadUIAssistOverlay };

const UIAssistOverlay = lazy(() => overlayLoader.load());

/**
 * `enabled` is true only when the server has BOTH `ENABLE_UI_ASSIST` and
 * `ENABLE_UI_ASSIST_OVERLAY` set (Sprint 63.22.0 decision 11); by default the
 * overlay chunk is never requested. `relayUrl` is the shared field name the
 * backend sends (decision 13).
 */
type UIAssistStatus = UIAssistStatusResponse;

/**
 * The toast clearance is not written here.
 *
 * Sprint 61.0.10.4 AC11 made this provider raise `--dm-toast-clearance` while
 * the toolbar was mounted, because the toolbar was `position: fixed` in the
 * toast stack's corner. Sprint 63.22.0 (decision 3) gave the corner one owner:
 * the toolbar renders into a `BottomRightDock` slot, and the dock — which
 * measures its whole row, star and toolbar together — is the only writer of
 * the variable. A second writer here would remove the dock's clearance on
 * unmount while the launcher is still in the corner. Held by
 * `dashboard-finish-61.0.10.4.test.ts` (AC11: one writer, no named exceptions).
 */
export function UIAssistProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<UIAssistStatus | null>(null);
  const overlayMounted = Boolean(status?.enabled && status?.hasPermission);

  useEffect(() => {
    let mounted = true;

    apiClient
      .getUIAssistStatus()
      .then((data) => {
        if (mounted) setStatus(data);
      })
      .catch(() => {
        // Feature not available — silently disable
        if (mounted) setStatus({ enabled: false, hasPermission: false, relayUrl: null });
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      {children}
      {overlayMounted && (
        <Suspense fallback={null}>
          <UIAssistOverlay relayUrl={status?.relayUrl ?? null} />
        </Suspense>
      )}
    </>
  );
}
